import { ReactElement, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import firstViewVideoJa from '@/assets/images/articles/cta/firstview.webm';
import firstViewVideoEn from '@/assets/images/articles/cta/firstview_en.webm';
import { useLazyLoad } from '@/hooks/useLazyLoad';

/**
 * 記事下に設置する紹介用のコンポーネント
 */
function AppCallToAction({ variant = 'full' }: { variant?: 'full' | 'inline' }): ReactElement {
    const videoRef = useRef<HTMLVideoElement>(null);
    // 遅延読み込み
    const [ref] = useLazyLoad<HTMLDivElement>({ threshold: 0.1 });
    const location = useLocation();
    const pathname = location.pathname;
    const isEnglishArticle =
        pathname.startsWith('/en/articles/') || /\/articles\/en_/.test(pathname);
    const ctaDemoVideoSrc = isEnglishArticle ? firstViewVideoEn : firstViewVideoJa;

    const copy = isEnglishArticle
        ? {
              title: (
                  <>
                      See where your time goes<br />
                      from your calendar at a glance📊🔍
                  </>
              ),
              description:
                  'With Kotomil, just connect Google Calendar to see how much time you spend on each area in charts and lists. It also helps you find what is making you busy and where your time is becoming unbalanced.',
              bullet1: 'Understand where your time is going at a glance',
              bullet2: 'Review your monthly time balance more clearly',
              bullet3: 'Notice time-use patterns with keyword summaries',
              support: 'Works with Google Calendar and Outlook Calendar.',
              linkLabel: 'Try Kotomil for free',
          }
        : {
              title: (
                  <>
                      何に時間を使っているか、<br />
                      カレンダーからひと目で分かる📊🔍
                  </>
              ),
              description:
                  'コトミルなら、Googleカレンダーを連携するだけで、何にどれくらい時間を使っているかをグラフや一覧で確認できます。忙しさの原因や、時間の偏りも見つけやすくなります。',
              bullet1: '何に時間を使っているかがひと目で分かる',
              bullet2: '月ごとの時間配分を振り返れる',
              bullet3: 'キーワード別の集計で使い方の偏りに気づける',
              support: 'Googleカレンダー / Outlookカレンダーに対応',
              linkLabel: '無料でコトミルを試す',
          };

    // メイン処理
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 1;
        }
    }, [isEnglishArticle]);

    if (variant === 'inline') {
        return (
            <aside className="article-inline-cta" aria-label={isEnglishArticle ? 'Try Kotomil' : 'コトミルを試す'}>
                <p className="article-inline-cta-title">{copy.title}</p>
                <p className="article-inline-cta-description">{copy.description}</p>
                <a
                    className="article-inline-cta-link"
                    href={`/?from=${encodeURIComponent(location.pathname + location.search)}`}
                >
                    {copy.linkLabel}
                </a>
            </aside>
        );
    }

    return (
        <>
            <div className="mt-14 -mx-5 border-t-[6px] border-dashed border-[#e5e7eb] px-5 pt-7 text-base md:-mx-12 md:px-12 lg:mt-16 lg:pt-10 lg:text-lg">
                {/* タイトル */}
                <h2 className="mx-1 rounded-2xl border border-[#f0df9a] bg-[#fff8dc] px-4 py-4 text-center text-[1.4rem] font-medium leading-normal tracking-wide text-gray-700 md:mx-2 lg:px-5 lg:py-5 lg:text-[1.75rem] lg:leading-relaxed">
                    {copy.title}
                </h2>
                <p className="mt-6 mx-6 tracking-widest leading-relaxed">{copy.description}</p>
                {/* 説明 */}
                <div className="pt-2 px-6">
                    <ul className="list-disc pl-4 mt-6 space-y-2 tracking-widest leading-relaxed">
                        <li>{copy.bullet1}</li>
                        <li>{copy.bullet2}</li>
                        <li>{copy.bullet3}</li>
                    </ul>
                    {/* リンク */}
                    <div className="flex flex-col items-center justify-center pt-9">
                        <a
                            className="inline-flex w-auto items-center justify-center rounded-full bg-[#ffd84d] px-7 py-3 text-center text-base font-semibold tracking-wide text-[#17233f] transition hover:bg-[#f7c927] hover:translate-y-[-1px] focus:outline-none focus:ring-4 focus:ring-yellow-200 lg:px-8 lg:py-3.5 lg:text-lg"
                            href={`/?from=${encodeURIComponent(location.pathname + location.search)}`}
                        >
                            {copy.linkLabel}
                        </a>
                        <p className="mt-4 text-center text-sm leading-relaxed tracking-widest text-gray-500 lg:text-base">
                            {copy.support}
                        </p>
                    </div>
                </div>
                {/* メディア */}
                <div ref={ref}>
                    <video
                        key={isEnglishArticle ? 'en' : 'ja'}
                        ref={videoRef}
                        src={ctaDemoVideoSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="mt-8 mx-auto w-full rounded-2xl border-4 border-white shadow-lg image-protection"
                    />
                </div>
            </div>
        </>
    );
}

export default AppCallToAction;
